import { redactCharacter } from '../../utils/constants'
import {
  type FormItemFullValue,
  type FormItemString,
  type FormItemTwoDimensional,
  type FullValueFormItemType,
  fullValueFormItemTypes,
  type RedactedTextsState,
  type RedactText,
  type RedactTextType,
  type RedactTwoDimensionalTextType,
  type SimpleTextType,
  type SpecialTextType,
  type StringFormItemType,
  simpleTextTypeDef,
  specialTextTypeDef,
  stringFormItemTypes,
  type TwoDimensionalFormItemType,
  twoDimensionalFormItemTypes,
  type UsernameTextType,
  usernameTextTypeDef,
} from './redactionConstants'

export const redactText: (
  redactedTexts: RedactedTextsState,
  textType: RedactTextType,
  text: RedactText,
  defaultText: string
) => RedactedTextsState = (redactedTexts, textType, text, defaultText) => {
  if (usernameTextTypeDef.includes(textType.type as UsernameTextType)) {
    const usernames = getUsernameObjectByTextType(redactedTexts, textType)
    const updatedUsernames = updateUsernames(usernames, text, textType)
    return putUsernameByTextType(redactedTexts, textType, updatedUsernames)
  }
  const originalText: string = getTextByTextType(redactedTexts, textType, defaultText)
  const updatedText: string = updateText(originalText, textType, text, defaultText)
  return putTextByTextType(redactedTexts, textType, updatedText)
}

/**
 * Get the text content for a specific text type from the redacted texts.
 * @returns Text content or default text if not found
 */
export function getTextByTextType(
  redactedTexts: RedactedTextsState,
  textType: RedactTextType,
  defaultText: string
): string
/**
 * Get the text content for a specific text type from the redacted texts.
 * @returns Text content or `undefined` if not found
 */
export function getTextByTextType(
  redactedTexts: RedactedTextsState,
  textType: RedactTextType
): string | undefined
export function getTextByTextType(
  redactedTexts: RedactedTextsState,
  textType: RedactTextType,
  defaultText?: string
): string | undefined {
  if (simpleTextTypeDef.includes(textType.type as SimpleTextType)) {
    return redactedTexts[textType.type as SimpleTextType] ?? defaultText
  }
  if (specialTextTypeDef.includes(textType.type as SpecialTextType)) {
    return redactedTexts[textType.type as SpecialTextType]?.[textType.id ?? ''] ?? defaultText
  }
  if (textType.type === 'formItems') {
    const commonFormItem = redactedTexts.formItems?.[textType.id]
    if (
      stringFormItemTypes.includes(textType.formItem as StringFormItemType) ||
      fullValueFormItemTypes.includes(textType.formItem as FullValueFormItemType)
    ) {
      const formItem = commonFormItem as FormItemFullValue | FormItemString
      return formItem?.value ?? defaultText
    }
    if (twoDimensionalFormItemTypes.includes(textType.formItem as TwoDimensionalFormItemType)) {
      const formItem = commonFormItem as FormItemTwoDimensional
      const typedTextType = textType as RedactTwoDimensionalTextType
      return formItem?.value?.[typedTextType.itemId] ?? defaultText
    }
    throw new Error(`getTextByTextType: Unsupported form item type: ${textType.formItem}`)
  }
  throw new Error(`getTextByTextType: Unsupported text type: ${textType.type}`)
}

export const updateText = (
  originalText: string,
  textType: RedactTextType,
  text: RedactText,
  defaultText: string
) => {
  if (usernameTextTypeDef.includes(textType.type as UsernameTextType)) {
    throw new Error('updateText: Username text type is not supported')
  }

  if (text.variant === 'redacted') {
    // We replace selection with █
    return (
      originalText.substring(0, text.start) +
      redactCharacter.repeat(text.end - text.start) +
      originalText.substring(text.end)
    )
  }
  // We replace selection with original text
  return (
    originalText.substring(0, text.start) +
    defaultText.substring(text.start, text.end) +
    originalText.substring(text.end)
  )
}

export const putTextByTextType: (
  redactedTexts: RedactedTextsState,
  textType: RedactTextType,
  text: string
) => RedactedTextsState = (redactedTexts, textType, text) => {
  if (usernameTextTypeDef.includes(textType.type as UsernameTextType)) {
    throw new Error('putTextByTextType: Username text type is not supported')
  }
  if (simpleTextTypeDef.includes(textType.type as SimpleTextType)) {
    return {
      ...redactedTexts,
      [textType.type as SimpleTextType]: text,
    }
  }
  if (specialTextTypeDef.includes(textType.type as SpecialTextType)) {
    return {
      ...redactedTexts,
      [textType.type as SpecialTextType]: {
        ...redactedTexts[textType.type as SpecialTextType],
        [textType.id ?? '']: text,
      },
    }
  }
  if (textType.type === 'formItems') {
    if (
      stringFormItemTypes.includes(textType.formItem as StringFormItemType) ||
      fullValueFormItemTypes.includes(textType.formItem as FullValueFormItemType)
    ) {
      return {
        ...redactedTexts,
        formItems: {
          ...redactedTexts.formItems,
          [textType.id]: {
            type: textType.formItem as FullValueFormItemType | StringFormItemType,
            value: text,
          },
        },
      }
    }
    if (twoDimensionalFormItemTypes.includes(textType.formItem as TwoDimensionalFormItemType)) {
      const typedTextType = textType as RedactTwoDimensionalTextType
      const existingValues = (redactedTexts.formItems?.[textType.id]?.value ??
        {}) as FormItemTwoDimensional['value']
      return {
        ...redactedTexts,
        formItems: {
          ...(redactedTexts.formItems ?? {}),
          [textType.id]: {
            type: textType.formItem as TwoDimensionalFormItemType,
            value: {
              ...existingValues,
              [typedTextType.itemId]: text,
            },
          } as FormItemTwoDimensional,
        },
      }
    }
    throw new Error(`putTextByTextType: Unsupported form item type: ${textType.formItem}`)
  }
  throw new Error(`putTextByTextType: Unsupported text type: ${textType.type}`)
}

export const getUsernameObjectByTextType: (
  redactedTexts: RedactedTextsState,
  textType: RedactTextType
) => Record<string, string> = (redactedTexts, textType) => {
  if (!usernameTextTypeDef.includes(textType.type as UsernameTextType)) {
    throw new Error(`Only username text types are supported, got: ${textType.type}`)
  }
  return (redactedTexts[textType.type] as Record<string, string>) ?? {}
}

export const updateUsernames: (
  usernames: Record<string, string>,
  text: RedactText,
  textType: RedactTextType
) => Record<string, string> = (usernames, text, textType) => {
  if (!usernameTextTypeDef.includes(textType.type as UsernameTextType)) {
    throw new Error(`Only username text types are supported, got: ${textType.type}`)
  }
  if (text.variant === 'redacted' && textType.id) {
    usernames[textType.id] = usernames[textType.id] ?? ''
  }
  const usernameIds = Object.keys(usernames).filter(
    text.variant === 'notRedacted'
      ? id => {
          return id !== textType.id
        }
      : () => true
  )
  return usernameIds.reduce(
    (acc, id, key) => {
      acc[id] = (key + 1).toString()
      return acc
    },
    {} as Record<string, string>
  )
}

export const putUsernameByTextType: (
  redactedTexts: RedactedTextsState,
  textType: RedactTextType,
  usernames: Record<string, string>
) => RedactedTextsState = (redactedTexts, textType, usernames) => {
  if (!usernameTextTypeDef.includes(textType.type as UsernameTextType)) {
    throw new Error(`Only username text types are supported, got: ${textType.type}`)
  }
  return {
    ...redactedTexts,
    [textType.type]: usernames,
  }
}
